// src/api/mfa/index.ts

import apiInstance from "shared/api/axiosConfig";
import { decryptApiBodyToJson } from "shared/api/cryptApiBody";

export interface MFASetupResponse {
    qrCode?: string;
    secret?: string;
    message: string;
    expiresIn?: number;
}

export interface BackupCodesResponse {
    backupCodes: string[];
}

export interface SessionInfo {
    id: number;
    sessionId: string;
    isCurrent: boolean;
    deviceInfo: {
        browser: string;
        os: string;
        device: string;
        deviceName: string;
    };
    ipAddress: string;
    lastActivity: Date;
    lastActivityFormatted: string;
}

export interface IdProtectSetupResponse {
    hasToken: boolean;
    message: string;
}

export interface IdProtectConfirmationResponse {
    confirmed: boolean;
    message?: string;
}

export const mfaService = {
    // Setup email MFA (sends test code)
    setupEmailMFA: async (userType = "individual"): Promise<MFASetupResponse> => {
        const response = await apiInstance.post('/client/mfa/setup/email', {type: userType});
        return response.data;
    },

    resendEmailVerificationCode: async (email: string): Promise<MFASetupResponse> => {
        const response = await apiInstance.post('/client/mfa/reSendEmailVerificationCode', {email: email});
        return response.data;
    },

    // Setup mobile MFA (generates QR code)
    setupMobileMFA: async (userType = "individual"): Promise<MFASetupResponse> => {
        const response = await apiInstance.post('/client/mfa/setup/mobile', {type: userType});
        return response.data;
    },

    // Setup ID Protect MFA (check token device and send push notification)
    setupIdProtectMFA: async (userType = "individual"): Promise<IdProtectSetupResponse> => {
        const response = await apiInstance.post('/client/mfa/setup/idprotect', {type: userType});
        return response.data;
    },

    // Check ID Protect confirmation status (polling)
    checkIdProtectConfirmation: async (userType = "individual"): Promise<IdProtectConfirmationResponse> => {
        const response = await apiInstance.post('/client/mfa/check-idprotect-confirmation', {type: userType});
        return response.data;
    },

    // Resend push notification for ID Protect MFA
    resendPushNotification: async (mfaToken: string): Promise<{ message: string }> => {
        const response = await apiInstance.post(`/client/mfa/resend-push-notification`, {mfaToken});
        return response.data;
    },

    polling: async (mfaToken: string): Promise<any> => {
        const response = await apiInstance.post(`/client/mfa/poll`, {mfaToken});
        return decryptApiBodyToJson(response.data);
    },

    // Enable MFA with verification code
    enableMFA: async (mfaType: string, code: string, tempSecret?: string, userType = "individual"): Promise<{ backupCodes: string[] }> => {
        const response = await apiInstance.post('/client/mfa/enable', { mfaType, code, tempSecret, type: userType });

        return response.data;
    },

    // Verify MFA during login
    verifyMFALogin: async (mfaToken: string, code: string, mfaType: string, userType = "individual"): Promise<any> => {
        const response = await apiInstance.post('/client/mfa/verify-login', { mfaToken, code, mfaType, type: userType });

        localStorage.setItem("type", decryptApiBodyToJson(response.data).type);
        return decryptApiBodyToJson(response.data);
    },

    // Disable MFA
    disableMFA: async (code: string, mfaType: string, userType = "individual"): Promise<void> => {
        await apiInstance.post('/client/mfa/disable', { code, mfaType, type: userType });
    },

    // Get MFA status
    getMFAStatus: async (userType = "individual"): Promise<{ isEnabled: boolean; type: string; hasBackupCodes: boolean }> => {
        const response = await apiInstance.post('/client/mfa/status', {type: userType });
        return response.data;
    },

    // Get all user sessions
    getSessions: async (userType = "individual"): Promise<{ sessions: SessionInfo[]; currentSessionId: string }> => {
        const response = await apiInstance.post('/client/sessions/list', {type: userType });
        return response.data;
    },

    // Revoke a specific session
    revokeSession: async (sessionId: string, userType = "individual"): Promise<void> => {
        await apiInstance.delete(`/client/sessions/revoke/${sessionId}`,  {
            data: { type: userType },
        });
    },

    // Revoke all other sessions
    revokeAllSessions: async (userType = "individual"): Promise<{ count: number }> => {
        const response = await apiInstance.post('/client/sessions/revoke-all', {type: userType });
        return response.data;
    },

    // Report suspicious session
    reportSession: async (sessionId: string, reason?: string, userType = "individual"): Promise<{ recommendPasswordChange: boolean }> => {
        const response = await apiInstance.post(`/client/sessions/report/${sessionId}`, { reportReason: reason, type: userType });
        return response.data;
    }
};